import { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Divider, IconButton, Input, Modal,
  Option, Select, Sheet, Stack, Table, Typography,
} from '@mui/joy';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { useAdmin } from '../../AdminContext';
import {
  APIAdminPartners, APIAdminPartnerDetail, APIAdminPartnerUpdate,
  APIAdminPartnerActivate, APIAdminPartnerDeactivate,
} from '../../api_admin_partners';

const money = (v) => `${Number(v || 0).toLocaleString('ru-RU')} ₽`;

function PartnerModal({ token, partnerId, open, onClose, onSaved }) {
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !partnerId) return;
    let alive = true;
    setLoading(true); setError(null);
    (async () => {
      try { const r = await APIAdminPartnerDetail(token, partnerId); if (alive) setP(r?.partner || null); }
      catch { if (alive) setError('Не удалось загрузить партнёра'); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [open, partnerId, token]);

  const set = (k, v) => setP((prev) => ({ ...prev, [k]: v }));

  const save = async () => {
    setBusy(true); setError(null);
    try {
      await APIAdminPartnerUpdate(token, partnerId, {
        full_name: p.full_name, phone: p.phone, scope_type: p.scope_type,
        scope_city: p.scope_city, scope_region: p.scope_region,
        commission_percent: Number(p.commission_percent) || 0,
      });
      onSaved?.();
    } catch { setError('Не удалось сохранить (нужна роль superadmin/finance)'); }
    finally { setBusy(false); }
  };
  const toggleActive = async () => {
    setBusy(true);
    try {
      if (p.is_active) await APIAdminPartnerDeactivate(token, partnerId);
      else await APIAdminPartnerActivate(token, partnerId);
      onSaved?.();
    } finally { setBusy(false); }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Sheet variant="outlined" sx={{
        position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
        width: { xs: '94%', sm: 540 }, maxHeight: '88vh', overflowY: 'auto', borderRadius: 'lg', p: 3,
      }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography level="h4">Партнёр {partnerId ? `#${partnerId}` : ''}</Typography>
          <IconButton size="sm" variant="plain" onClick={onClose}><CloseRoundedIcon /></IconButton>
        </Stack>

        {loading ? (
          <Stack alignItems="center" sx={{ py: 5 }}><CircularProgress /></Stack>
        ) : !p ? (
          <Alert color="danger">{error || 'Нет данных'}</Alert>
        ) : (
          <Stack spacing={1.3}>
            {error && <Alert color="danger">{error}</Alert>}
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip size="sm" color={p.is_active ? 'success' : 'neutral'} variant="soft">
                {p.is_active ? 'Активен' : 'Отключён'}
              </Chip>
              <Chip size="sm" variant="soft" color="neutral">{p.email}</Chip>
            </Stack>

            <Stack direction="row" spacing={2} flexWrap="wrap">
              <Box><Typography level="body-xs" color="neutral">Баланс</Typography>
                <Typography level="body-sm">{money(p.balance)}</Typography></Box>
              <Box><Typography level="body-xs" color="neutral">Заработано</Typography>
                <Typography level="body-sm">{money(p.total_earned)}</Typography></Box>
              <Box><Typography level="body-xs" color="neutral">Выплачено</Typography>
                <Typography level="body-sm">{money(p.total_paid_out)}</Typography></Box>
            </Stack>

            <Divider />

            <Box><Typography level="body-xs" color="neutral">ФИО</Typography>
              <Input value={p.full_name || ''} onChange={(e) => set('full_name', e.target.value)} /></Box>
            <Box><Typography level="body-xs" color="neutral">Телефон</Typography>
              <Input value={p.phone || ''} onChange={(e) => set('phone', e.target.value)} /></Box>
            <Stack direction="row" spacing={1}>
              <Box sx={{ flex: 1 }}><Typography level="body-xs" color="neutral">Тип охвата</Typography>
                <Input value={p.scope_type || ''} onChange={(e) => set('scope_type', e.target.value)} /></Box>
              <Box sx={{ flex: 1 }}><Typography level="body-xs" color="neutral">Комиссия, %</Typography>
                <Input type="number" value={p.commission_percent ?? ''} onChange={(e) => set('commission_percent', e.target.value)} /></Box>
            </Stack>
            <Stack direction="row" spacing={1}>
              <Box sx={{ flex: 1 }}><Typography level="body-xs" color="neutral">Город</Typography>
                <Input value={p.scope_city || ''} onChange={(e) => set('scope_city', e.target.value)} /></Box>
              <Box sx={{ flex: 1 }}><Typography level="body-xs" color="neutral">Регион</Typography>
                <Input value={p.scope_region || ''} onChange={(e) => set('scope_region', e.target.value)} /></Box>
            </Stack>

            <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
              <Button color="primary" loading={busy} onClick={save}>Сохранить</Button>
              <Button color={p.is_active ? 'danger' : 'success'} variant="soft" loading={busy} onClick={toggleActive}>
                {p.is_active ? 'Отключить' : 'Активировать'}
              </Button>
            </Stack>
          </Stack>
        )}
      </Sheet>
    </Modal>
  );
}

export default function AdminPartnersPage() {
  const { adminToken: token } = useAdmin();
  const [items, setItems] = useState([]);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [activeF, setActiveF] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editId, setEditId] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => { setPage(1); setSearch(searchInput); }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true); setError(null);
    try {
      const r = await APIAdminPartners(token, {
        search: search || undefined, is_active: activeF || undefined, page, page_size: 20,
      });
      setItems(r?.items || []); setPages(r?.pages || 1); setTotal(r?.total || 0);
    } catch { setError('Не удалось загрузить партнёров'); } finally { setLoading(false); }
  }, [token, search, activeF, page]);
  useEffect(() => { load(); }, [load]);

  return (
    <Box>
      <Typography level="h4" sx={{ mb: 0.5 }}>Партнёры</Typography>
      <Typography level="body-sm" color="neutral" sx={{ mb: 2 }}>Всего: {total}</Typography>

      <Sheet variant="outlined" sx={{ p: 1.5, borderRadius: 'md', mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <Input size="sm" placeholder="ФИО или email" value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)} sx={{ flex: 1 }} />
          <Select size="sm" value={activeF} onChange={(_, v) => { setPage(1); setActiveF(v); }} sx={{ minWidth: 170 }}>
            <Option value="">Все</Option>
            <Option value="true">Активные</Option>
            <Option value="false">Отключённые</Option>
          </Select>
        </Stack>
      </Sheet>

      {error && <Alert color="danger" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress /></Stack>
      ) : (
        <Sheet variant="outlined" sx={{ borderRadius: 'md', overflow: 'auto' }}>
          <Table hoverRow sx={{ '--TableCell-paddingY': '10px' }}>
            <thead>
              <tr>
                <th style={{ width: 60 }}>ID</th><th>ФИО</th><th>Email</th>
                <th style={{ width: 110 }}>Комиссия</th><th style={{ width: 130 }}>Баланс</th>
                <th style={{ width: 110 }}>Статус</th><th style={{ width: 110 }} />
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id}>
                  <td>{p.id}</td>
                  <td>{p.full_name}</td>
                  <td>{p.email}</td>
                  <td>{p.commission_percent}%</td>
                  <td>{money(p.balance)}</td>
                  <td>
                    <Chip size="sm" color={p.is_active ? 'success' : 'neutral'} variant="soft">
                      {p.is_active ? 'Активен' : 'Отключён'}
                    </Chip>
                  </td>
                  <td><Button size="sm" variant="soft" onClick={() => setEditId(p.id)}>Открыть</Button></td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={7}>
                  <Typography level="body-sm" color="neutral" sx={{ py: 2, textAlign: 'center' }}>Ничего не найдено</Typography>
                </td></tr>
              )}
            </tbody>
          </Table>
        </Sheet>
      )}

      {pages > 1 && (
        <Stack direction="row" spacing={1} justifyContent="center" alignItems="center" sx={{ mt: 2 }}>
          <Button size="sm" variant="outlined" disabled={page <= 1} onClick={() => setPage((x) => x - 1)}>Назад</Button>
          <Chip variant="soft">{page} / {pages}</Chip>
          <Button size="sm" variant="outlined" disabled={page >= pages} onClick={() => setPage((x) => x + 1)}>Вперёд</Button>
        </Stack>
      )}

      <PartnerModal
        token={token} partnerId={editId} open={!!editId}
        onClose={() => setEditId(null)} onSaved={() => { setEditId(null); load(); }}
      />
    </Box>
  );
}